import type { ModalProps } from 'naive-ui';
import type { Component, ComputedRef } from 'vue';
import { nanoid } from 'nanoid';
import { camelize, computed, inject, markRaw, provide, ref } from 'vue';

export interface ModalOptions {
  component: Component;
  closeEvents?: string[];
  props?: Record<string, unknown>;
  modalProps?: ModalProps;
}

export interface ModalInstance extends Omit<ModalOptions, 'closeEvents'> {
  id: string;
  modalProps: Record<string, unknown>;
}

export interface ModalValue<T = any> {
  event: string;
  data: T;
}

export interface ModalProvider {

  open<T = any>(options: ModalOptions): Promise<ModalValue<T>>;
}

const modalManagerToken = Symbol.for('modalManager');

export function createModalManager(): {
  modals: ComputedRef<ModalInstance[]>;
} {
  const modals = ref<ModalInstance[]>([]);

  function open({ component, props, modalProps, closeEvents }: ModalOptions) {
    if (!modalProps) {
      modalProps = {
        closeOnEsc: true,
        maskClosable: true,
      };
    }

    return new Promise((resolve) => {
      const instance: ModalInstance = {
        id: nanoid(),
        component: markRaw(component),
        props: props || {},
        modalProps: modalProps as Record<string, unknown>,
      };

      for (const eventName of closeEvents || ['close']) {
        instance.props![camelize(`on-${eventName}`)] = (data: unknown) => {
          resolve({ event: eventName, data });
          close(instance);
        };
      }

      instance.modalProps.onEsc = (event: unknown) => {
        if (!modalProps?.closeOnEsc)
          return;
        resolve({ event: 'nativeClose', data: event });
        close(instance);
      };

      instance.modalProps.onMaskClick = (event: unknown) => {
        if (!modalProps?.maskClosable)
          return;
        resolve({ event: 'nativeClose', data: event });
        close(instance);
      };

      modals.value.push(instance);
    });
  }

  function close(instance: ModalInstance) {
    const index = modals.value.findIndex(item => item.id === instance.id);
    if (index >= 0) {
      modals.value.splice(index, 1);
    }
  }

  provide(modalManagerToken, <ModalProvider>{
    open,
  });

  return {
    modals: computed(() => modals.value),
  };
}

export function useModal() {
  return inject<ModalProvider>(modalManagerToken)!;
}
